/**
 * 获取模板工具
 * 
 * 设计理念：提供模板内容给 AI，AI 自己决定如何使用和修改
 * 不做复杂的渲染逻辑，让 AI 发挥其强大的代码理解和修改能力
 * 
 * @module tools/getTemplate
 */

import { 
    getTemplateById,
    getTemplateDir 
} from '../core/templates/discovery';
import { ConsoleLogger } from '../core/types';

/**
 * 获取模板
 * 
 * @example
 * // 获取模板配置说明（不含文件内容，节省 token）
 * get_template({ id: 'vue/api-layer' })
 * 
 * @example
 * // 获取完整模板（含所有文件内容）
 * get_template({ id: 'vue/api-layer', includeFiles: true })
 * 
 * @example
 * // 获取特定文件
 * get_template({ id: 'vue/api-layer', files: ['request.ts', 'types.ts'] })
 */
export async function getTemplate(args: {
    /** 模板 ID */
    id: string;
    /** 是否包含文件内容（默认 false，只返回配置说明） */
    includeFiles?: boolean;
    /** 指定要获取的文件列表（可选，不填则获取全部） */
    files?: string[];
}): Promise<{
    content: Array<{ type: string; text: string }>;
}> {
    const logger = new ConsoleLogger();
    
    try {
        const result = getTemplateById(args.id, args.includeFiles ?? false);
        
        if (!result) {
            return {
                content: [{
                    type: 'text',
                    text: JSON.stringify({
                        error: `模板 "${args.id}" 不存在`,
                        hint: '使用 list_templates 查看可用模板'
                    }, null, 2)
                }]
            };
        }
        
        // 如果指定了特定文件，过滤文件列表
        if (args.files && result.files) {
            result.files = result.files.filter(f => 
                args.files!.some(requested => 
                    f.path === requested || f.path.endsWith(requested)
                )
            );
        }
        
        // 获取模板目录路径（方便 AI 直接操作）
        const templateDir = getTemplateDir(args.id);
        
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    success: true,
                    template: {
                        ...result,
                        absolutePath: templateDir
                    },
                    aiGuidance: {
                        step1: '阅读 configGuide 了解配置要求',
                        step2: '根据目标项目情况决定如何修改模板',
                        step3: '将文件复制到目标项目并适配',
                        note: '模板文件可能需要根据项目实际情况调整（如 UI 框架、后端响应格式等）'
                    }
                }, null, 2)
            }]
        };
    } catch (error) {
        logger.error(`获取模板失败: ${error}`);
        return {
            content: [{
                type: 'text',
                text: JSON.stringify({
                    error: error instanceof Error ? error.message : String(error)
                }, null, 2)
            }]
        };
    }
}
